import { useEffect, useState } from 'react';
import { api } from '../../../apis/api';
import { InvoiceDocument } from '../../components/invoiceDocument/invoiceDocument';
import './orders.css';
import '../categories/categories.css';
import '../../components/invoiceDocument/invoiceDocument.css';

const ORDER_STATUSES = [
  'enquire',
  'placed',
  'pending',
  'confirmed',
  'shipped',
  'delivered',
  'cancelled',
];

const statusColors = {
  enquire: 'status--enquire',
  placed: 'status--pending',
  pending: 'status--pending',
  confirmed: 'status--shipped',
  shipped: 'status--shipped',
  delivered: 'status--delivered',
  cancelled: 'status--cancelled',
};

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');

export const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewing, setViewing] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getOrders(filter ? { status: filter } : {});
      setOrders(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (err) {
      setOrders([]);
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (orderId, status) => {
    try {
      await api.updateOrderStatus(orderId, status);
      await load();
      if (viewing?.order_id === orderId) {
        const res = await api.getOrder(orderId);
        setViewing(res.data);
      }
    } catch (err) {
      alert(err.message || 'Failed to update status');
    }
  };

  const openView = async (orderId) => {
    setViewLoading(true);
    try {
      const res = await api.getOrder(orderId);
      setViewing(res.data);
    } catch (err) {
      alert(err.message || 'Failed to load order');
    } finally {
      setViewLoading(false);
    }
  };

  const downloadInvoice = async (orderId, invoiceNo) => {
    setDownloadingId(orderId);
    try {
      await api.downloadInvoicePdf(orderId, invoiceNo);
    } catch (err) {
      alert(err.message || 'Failed to download invoice');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) return <p className="admin-loading">Loading orders…</p>;
  if (error) return <p className="admin-error">{error}</p>;

  return (
    <div className="admin-orders">
      <div className="admin-orders__filters">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="admin-select">
          <option value="">All Status</option>
          {ORDER_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>INVOICE</th>
              <th>CUSTOMER</th>
              <th>DATE</th>
              <th>STATUS</th>
              <th>TOTAL</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan={6}>No orders found</td></tr>
            ) : (
              orders.map((o) => (
                <tr key={o.order_id}>
                  <td>{o.invoice_no}</td>
                  <td>{o.customer_name}</td>
                  <td>{formatDate(o.created_at)}</td>
                  <td>
                    <span className={`status-badge ${statusColors[o.status] || 'status--pending'}`}>
                      {o.status}
                    </span>
                  </td>
                  <td>{formatINR(o.total)}</td>
                  <td className="admin-table__actions">
                    <button
                      type="button"
                      className="admin-btn admin-btn--outline"
                      onClick={() => openView(o.order_id)}
                      disabled={viewLoading}
                    >
                      View
                    </button>
                    <button
                      type="button"
                      className="admin-btn admin-btn--outline"
                      onClick={() => downloadInvoice(o.order_id, o.invoice_no)}
                      disabled={downloadingId === o.order_id}
                    >
                      {downloadingId === o.order_id ? '…' : 'PDF'}
                    </button>
                    <select
                      className="admin-select"
                      value={o.status}
                      onChange={(e) => updateStatus(o.order_id, e.target.value)}
                    >
                      {ORDER_STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {viewing && (
        <div className="admin-modal-backdrop" onClick={() => setViewing(null)}>
          <div
            className="admin-modal admin-invoice-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-invoice-modal__toolbar">
              <button
                type="button"
                className="admin-btn admin-btn--primary"
                onClick={() => downloadInvoice(viewing.order_id, viewing.invoice_no)}
                disabled={downloadingId === viewing.order_id}
              >
                {downloadingId === viewing.order_id ? 'Downloading…' : 'Download Invoice PDF'}
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--outline"
                onClick={() => setViewing(null)}
              >
                Close
              </button>
            </div>
            <InvoiceDocument order={viewing} />
          </div>
        </div>
      )}
    </div>
  );
};
